// All sound is synthesised with the Web Audio API: no audio files, nothing to download.

import { settings } from './util.js';

class Sfx {
  private ctx?: AudioContext;
  private master?: GainNode;
  private noise?: { src: AudioBufferSourceNode; gain: GainNode; filter: BiquadFilterNode; voice: OscillatorNode; voiceGain: GainNode; lfo: OscillatorNode };
  private drone?: { nodes: AudioNode[]; gain: GainNode };

  /** Must be called from a user gesture (iOS). */
  unlock() {
    if (!this.ctx) {
      const Ctx = window.AudioContext ?? (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
      if (!Ctx) return;
      this.ctx = new Ctx();
      this.master = this.ctx.createGain();
      this.master.gain.value = settings.sound ? 0.9 : 0;
      this.master.connect(this.ctx.destination);
      // iOS: play even when the ring/silent switch is on, where supported
      const nav = navigator as Navigator & { audioSession?: { type: string } };
      if (nav.audioSession) try { nav.audioSession.type = 'playback'; } catch { /* not supported */ }
    }
    void this.ctx.resume();
  }

  setEnabled(on: boolean) {
    settings.sound = on;
    if (this.master && this.ctx) this.master.gain.setTargetAtTime(on ? 0.9 : 0, this.ctx.currentTime, 0.05);
  }

  private get ok() { return !!this.ctx && !!this.master; }

  private noiseBuffer(seconds = 2): AudioBuffer {
    const ctx = this.ctx!;
    const buf = ctx.createBuffer(1, ctx.sampleRate * seconds, ctx.sampleRate);
    const d = buf.getChannelData(0);
    for (let i = 0; i < d.length; i++) d[i] = Math.random() * 2 - 1;
    return buf;
  }

  private env(gain: GainNode, peak: number, attack: number, release: number, t = this.ctx!.currentTime) {
    gain.gain.setValueAtTime(0, t);
    gain.gain.linearRampToValueAtTime(peak, t + attack);
    gain.gain.exponentialRampToValueAtTime(0.0001, t + attack + release);
  }

  private tone(freq: number, dur: number, type: OscillatorType = 'sine', peak = 0.15, when = 0) {
    if (!this.ok) return;
    const ctx = this.ctx!;
    const t = ctx.currentTime + when;
    const osc = ctx.createOscillator(); const g = ctx.createGain();
    osc.type = type; osc.frequency.setValueAtTime(freq, t);
    this.env(g, peak, 0.01, dur, t);
    osc.connect(g).connect(this.master!);
    osc.start(t); osc.stop(t + dur + 0.05);
  }

  /** Radio static for the opening. `clarity` 0 = pure noise, 1 = clear carrier. */
  startStatic() {
    if (!this.ok || this.noise) return;
    const ctx = this.ctx!;
    const src = ctx.createBufferSource(); src.buffer = this.noiseBuffer(); src.loop = true;
    const filter = ctx.createBiquadFilter(); filter.type = 'bandpass'; filter.frequency.value = 1800; filter.Q.value = 0.6;
    const gain = ctx.createGain(); gain.gain.value = 0;
    src.connect(filter).connect(gain).connect(this.master!);
    const voice = ctx.createOscillator(); voice.type = 'triangle'; voice.frequency.value = 196;
    const voiceGain = ctx.createGain(); voiceGain.gain.value = 0;
    const lfo = ctx.createOscillator(); lfo.frequency.value = 5.5;
    const lfoGain = ctx.createGain(); lfoGain.gain.value = 18;
    lfo.connect(lfoGain).connect(voice.frequency);
    voice.connect(voiceGain).connect(this.master!);
    src.start(); voice.start(); lfo.start();
    gain.gain.setTargetAtTime(0.16, ctx.currentTime, 0.3);
    this.noise = { src, gain, filter, voice, voiceGain, lfo };
  }

  setStatic(clarity: number) {
    if (!this.noise || !this.ctx) return;
    const t = this.ctx.currentTime;
    this.noise.gain.gain.setTargetAtTime(0.03 + 0.15 * (1 - clarity) ** 1.5, t, 0.08);
    this.noise.filter.frequency.setTargetAtTime(900 + 2600 * (1 - clarity), t, 0.1);
    this.noise.voiceGain.gain.setTargetAtTime(0.05 * clarity ** 2, t, 0.1);
  }

  stopStatic() {
    if (!this.noise || !this.ctx) return;
    const n = this.noise; const t = this.ctx.currentTime;
    n.gain.gain.setTargetAtTime(0, t, 0.15); n.voiceGain.gain.setTargetAtTime(0, t, 0.15);
    setTimeout(() => { n.src.stop(); n.voice.stop(); n.lfo.stop(); }, 900);
    this.noise = undefined;
  }

  /** Incoming message. */
  incoming() { this.tone(880, 0.12, 'sine', 0.08); this.tone(1320, 0.18, 'sine', 0.05, 0.07); }
  /** Outgoing message. */
  sent() { this.tone(520, 0.08, 'sine', 0.05); }
  /** Signal found. */
  lock() { [523, 659, 784].forEach((f, i) => this.tone(f, 0.5, 'triangle', 0.07, i * 0.09)); }
  success() { [392, 523, 659, 1046].forEach((f, i) => this.tone(f, 0.7, 'sine', 0.06, i * 0.11)); }
  wrong() { this.tone(160, 0.25, 'square', 0.04); this.tone(120, 0.3, 'square', 0.04, 0.12); }

  /** Dial click: a very short burst of filtered noise. */
  tick(strong = false) {
    if (!this.ok) return;
    const ctx = this.ctx!;
    const src = ctx.createBufferSource(); src.buffer = this.noiseBuffer(0.05);
    const f = ctx.createBiquadFilter(); f.type = 'highpass'; f.frequency.value = strong ? 1200 : 2500;
    const g = ctx.createGain(); this.env(g, strong ? 0.5 : 0.25, 0.001, strong ? 0.06 : 0.025);
    src.connect(f).connect(g).connect(this.master!); src.start();
  }

  glitch() {
    if (!this.ok) return;
    const ctx = this.ctx!;
    const src = ctx.createBufferSource(); src.buffer = this.noiseBuffer(0.7);
    const g = ctx.createGain(); g.gain.value = 0;
    const t = ctx.currentTime;
    for (let i = 0; i < 9; i++) g.gain.setValueAtTime(i % 2 ? 0 : 0.22, t + i * 0.06);
    g.gain.setValueAtTime(0, t + 0.6);
    src.connect(g).connect(this.master!); src.start();
    this.tone(70, 0.6, 'sawtooth', 0.06);
  }

  /** Low, uneasy drone for the ending. */
  startDrone() {
    if (!this.ok || this.drone) return;
    const ctx = this.ctx!;
    const gain = ctx.createGain(); gain.gain.value = 0; gain.connect(this.master!);
    const nodes: AudioNode[] = [];
    for (const [f, type] of [[55, 'sine'], [82.4, 'sine'], [110.6, 'triangle']] as const) {
      const o = ctx.createOscillator(); o.type = type; o.frequency.value = f;
      const og = ctx.createGain(); og.gain.value = type === 'triangle' ? 0.25 : 0.5;
      o.connect(og).connect(gain); o.start(); nodes.push(o);
    }
    gain.gain.setTargetAtTime(0.08, ctx.currentTime, 2);
    this.drone = { nodes, gain };
  }

  stopDrone() {
    if (!this.drone || !this.ctx) return;
    const d = this.drone; d.gain.gain.setTargetAtTime(0, this.ctx.currentTime, 0.8);
    setTimeout(() => d.nodes.forEach((n) => (n as OscillatorNode).stop()), 4000);
    this.drone = undefined;
  }

  stopAll() { this.stopStatic(); this.stopDrone(); }
}

export const sfx = new Sfx();

export function vibrate(pattern: number | number[]) {
  // Not available on iOS: always an extra, never required (opdrachten-browsertechniek.md §2)
  try { navigator.vibrate?.(pattern); } catch { /* ignore */ }
}
